import { MediaElement, MediaText, TextWrapping } from 'editor/src/store/design/types';
import { TextOverflowWarning, WarningLevel, WarningType } from 'editor/src/store/editorModules/warnings/types';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';

import { getRenderedTextObject } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

// GAPI-61892: laying a text out again is the expensive part, and the warnings are worked
// out again on every design change. Elements are immutable store objects, so one that
// has not changed keeps its answer.
const overflowCache = new WeakMap<MediaText, boolean>();

/** Whether a fixed frame takes effect on this text: plain wrapping text only (no autofit, lockBoxSize, curve or flexibleWidth). */
export function canHaveFixedTextFrame(element: MediaText) {
  const { lockBoxSize, wrapping, curve, flexibleWidth } = element.extra;
  return (
    !lockBoxSize && wrapping !== TextWrapping.Fit && wrapping !== TextWrapping.FitOneLine && !curve && !flexibleWidth
  );
}

/** Whether the text can overflow at all: a fixed frame (extra.fixedFrame) around plain wrapping text. */
export function hasFixedTextFrame(element: MediaText) {
  return !!element.extra.fixedFrame && canHaveFixedTextFrame(element);
}

/**
 * Lays the text out the way the canvas does (same FabricPathText, in mm) and tells
 * whether the wrapped lines need more height than the fixed frame has.
 */
export function isTextFrameOverflowing(element: MediaText): boolean {
  // Without the font's metrics nothing wraps, so the answer would be a guess: protected
  // uploaded fonts are never loaded client-side, and others are measured once they load.
  if (!hasFixedTextFrame(element) || !element.extra.text || !isFontFileLoaded(element.extra.fontFamily)) {
    return false;
  }

  const cached = overflowCache.get(element);
  if (cached !== undefined) {
    return cached;
  }

  const overflowing = getRenderedTextObject(element).isTextOverflowing();
  overflowCache.set(element, overflowing);
  return overflowing;
}

function getTextOverflowWarning(element: MediaElement, spreadIndex: number): TextOverflowWarning | undefined {
  if (element.type !== 'text' || element.hidden || !isTextFrameOverflowing(element)) {
    return undefined;
  }

  return {
    type: WarningType.TextOverflow,
    uuid: element.uuid,
    spreadIndex,
    level: WarningLevel.Medium,
  };
}

export default getTextOverflowWarning;
